import { listen, qs, qsa, add, remove, contains } from "@fluorescent/dom";
import { createFocusTrap } from "focus-trap";
import { on, emit } from "@/glow/events";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { provideResizeObserver } from "@/utils";
import QuickSearchResults from "@/lib/quick-search-results";
import { getStorage, setStorage } from "@/lib/storage";

const typeStorageKey = "quickSearch:lastTypeSelection";

const selectors = {
  quickSearch: "[data-quick-search]",
  form: "[data-quick-search-form]",
  input: "[data-quick-search-input]",
  clear: "[data-quick-search-clear]",
  productType: "[data-quick-search-product-type]",
  suggestedSearchLinks: "[data-suggested-search]",
  resultsDropdown: ".quick-search__results-wrapper",
  resultsContainer: "[data-quick-search-results]",
  resultsViewAllButtonText: ".quick-search__view-all-label",
  cancel: "[data-quick-search-cancel]",
  headerContent: ".header__content-desktop",
  fauxInput: "[data-quick-search-faux-input]",
  fauxInputText: "[data-quick-search-faux-text]",
  activeSearchContainer: "[data-search-active-container]",
  fauxSubmitButton: "[data-quick-search-faux-submit]",
  fauxProductType: "[data-quick-search-faux-product-type]",
  headerSearchButton: "[data-header-button-type='search']",
};

const classes = {
  searchActive: "quick-search__form--search-active",
  showTypeSelect: "quick-search--show-type-selector",
};

export default function (node, header) {
  if (!node) return;
  const { quickSearchScope } = node.dataset;
  const fauxInputEl = qs(selectors.fauxInput, node);
  const fauxInputTextEl = qs(selectors.fauxInputText, node);
  const fauxSubmitButton = qs(selectors.fauxSubmitButton, node);
  const fauxProductTypeEl = qs(selectors.fauxProductType, node);
  const activeSearchContainer = qs(selectors.activeSearchContainer, node);
  const formEl = qs(selectors.form, node);
  const inputEl = qs(selectors.input, formEl);
  const clearEl = qs(selectors.clear, formEl);
  const cancelEl = qs(selectors.cancel, formEl);
  const productTypeEl = qs(selectors.productType, formEl);
  const resultsContainerEl = qs(selectors.resultsContainer, formEl);
  const resultsDropdownEl = qs(selectors.resultsDropdown, formEl);
  const resultsViewAllButtonTextEl = qs(
    selectors.resultsViewAllButtonText,
    formEl
  );
  const suggestedSearchLinksEls = qsa(selectors.suggestedSearchLinks, formEl);

  const placeholderText = fauxInputEl.getAttribute("data-placeholder");
  const placeholderTextWithinType = fauxInputEl.getAttribute(
    "data-placeholder-when-type-active"
  );

  let headerContentEl = null;

  let headerRO = null;

  let typeChoice = null;

  const focusTrap = createFocusTrap(activeSearchContainer, {
    allowOutsideClick: true,
    initialFocus: inputEl,
  });

  let quickSearchResults;

  if (resultsDropdownEl) {
    quickSearchResults = QuickSearchResults(
      resultsDropdownEl,
      resultsContainerEl,
      resultsViewAllButtonTextEl
    );
  }

  const events = [
    listen(fauxInputEl, "click", _toggleSearch),
    listen(fauxSubmitButton, "click", () => {
      formEl.submit();
    }),
    listen(clearEl, "click", _handleReset),
    listen(inputEl, "input", _handleInput),
    listen(cancelEl, "click", _toggleSearch),
    listen(window, "click", evt => {
      const closestQuickSearch = evt.target.closest(selectors.quickSearch);
      const isSearchToggle = evt.target.closest(selectors.headerSearchButton);

      if (isSearchToggle) return;
      if (!closestQuickSearch && quickSearchIsOpen()) {
        _toggleSearch();
      }
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) {
        if (contains(formEl, classes.searchActive)) {
          _handleReset();
        } else {
          _toggleSearch();
        }
      }
    }),
  ];

  if (quickSearchScope === "desktop") {
    headerContentEl = qs(selectors.headerContent, header);
    _setLayoutVars();
    provideResizeObserver().then(({ ResizeObserver }) => {
      headerRO = new ResizeObserver(() => {
        _setLayoutVars();
      });
      headerRO.observe(header);
    });

    events.push(on("navBar:change", _handleNavBarChange));

    on(`window:below-960`, handleBreakpointChange);
  } else {
    events.push(
      on("mobile-quick-search:open", () => {
        _toggleSearch();
      })
    );
    on(`window:above-960`, handleBreakpointChange);
  }

  if (productTypeEl) {
    events.push(
      listen(productTypeEl, "change", event => {
        _handleTypeChoice(event.target.value);
        _handleInput();
      })
    );
    events.push(
      listen(fauxProductTypeEl, "change", event => {
        _handleTypeChoice(event.target.value);
        // Store the selection so we can restore it on hard load of full search results page
        setStorage(typeStorageKey, JSON.stringify(event.target.value));
      })
    );

    const previousTypeChoice = getStorage(typeStorageKey);
    if (previousTypeChoice !== null) {
      // Looping is necessary to validate the old type
      productTypeEl.querySelectorAll("option").forEach(optionEl => {
        if (optionEl.value == previousTypeChoice) {
          _handleTypeChoice(previousTypeChoice);
        }
      });
    }
  }

  if (suggestedSearchLinksEls.length) {
    events.push(listen(node, "click", _handleSuggestedSearch));
  }

  function quickSearchIsOpen() {
    return node.dataset.quickSearchActive === "true";
  }

  function _handleSuggestedSearch(evt) {
    const suggestedSearchLink = evt.target.closest(
      selectors.suggestedSearchLinks
    );
    if (!suggestedSearchLink) return;
    evt.preventDefault();
    inputEl.value = suggestedSearchLink.dataset.suggestedSearch;
    _handleInput();
  }

  function _handleInput() {
    if (inputEl.value === "") {
      reset();

      if (productTypeEl) {
        fauxProductTypeEl.value = productTypeEl.value;
      }
      return;
    }
    add(formEl, classes.searchActive);

    if (fauxInputEl.dataset.placeholderActive === "true") {
      fauxInputEl.dataset.placeholderActive = false;
    }

    // Map the faux button to have the same text as the real search input
    fauxInputTextEl.innerText = inputEl.value;

    if (quickSearchResults) {
      const settings = {
        searchTerm: inputEl.value,
        productType: productTypeEl?.value?.length ? productTypeEl.value : null,
      };
      quickSearchResults.getSearchResults(settings);
    }
  }

  function _handleReset(evt) {
    evt?.preventDefault();
    reset();
  }

  function _handleNavBarChange() {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        _setLayoutVars();
      });
    }, 300);
  }

  function _toggleSearch() {
    if (quickSearchIsOpen()) {
      node.dataset.quickSearchActive = false;
      document.body.dataset.quickSearchOpen = false;
      focusTrap.deactivate();

      if (quickSearchScope === "mobile") {
        hideHeaderOverlay();
        inputEl.blur();
        enableBodyScroll(node);
      }
    } else {
      emit("search:focused");
      node.dataset.quickSearchActive = true;
      document.body.dataset.quickSearchOpen = true;
      focusTrap.activate();

      if (quickSearchScope === "mobile") {
        showHeaderOverlay();
        // Focus is required to trigger keyboard on iOS
        inputEl.focus();
        inputEl.select();
        if (inputEl.value.length) {
          _handleInput(); // Run search to populate results if not empty
        }
        disableBodyScroll(node, {
          allowTouchMove: el => {
            while (el && el !== document.body) {
              if (el.getAttribute("data-scroll-lock-ignore") !== null) {
                return true;
              }
              el = el.parentNode;
            }
          },
          reserveScrollBarGap: true,
        });
      }
    }
  }

  function handleBreakpointChange() {
    if (quickSearchIsOpen()) _toggleSearch();
  }

  function _setLayoutVars() {
    window.requestAnimationFrame(() => {
      const searchBounds = node.getBoundingClientRect();
      const headerBounds = headerContentEl.getBoundingClientRect();

      _setVar(
        activeSearchContainer,
        "--quick-search-width",
        `${searchBounds.width}px`
      );

      _setVar(
        activeSearchContainer,
        "--quick-search-height",
        `${searchBounds.height}px`
      );

      _setVar(
        activeSearchContainer,
        "--quick-search-left",
        `${searchBounds.left}px`
      );
      _setVar(activeSearchContainer, "--header-left", `${headerBounds.left}px`);
    });
  }

  function _setVar(el, varName, varValue) {
    el.style.setProperty(varName, varValue);
  }

  function _handleTypeChoice(type) {
    typeChoice = type;
    productTypeEl.value = type;
    fauxProductTypeEl.value = type;
    _updatePlaceholder();
  }

  function _updatePlaceholder() {
    let placeholder;

    if (typeChoice && typeChoice !== "") {
      const typeChoicePlaceholder = placeholderTextWithinType.replace(
        "{{ type }}",
        typeChoice
      );
      placeholder = typeChoicePlaceholder;
    } else {
      placeholder = placeholderText;
    }

    fauxInputTextEl.innerText = placeholder;
    inputEl.placeholder = placeholder;
  }

  function reset() {
    inputEl.value = "";
    remove(formEl, classes.searchActive);
    if (resultsContainerEl) {
      resultsContainerEl.innerHTML = "";
    }
    _updatePlaceholder();
    fauxInputEl.dataset.placeholderActive = true;
  }

  function showHeaderOverlay() {
    emit("headerOverlay:show");
  }

  function hideHeaderOverlay() {
    emit("headerOverlay:hide");
  }

  function open() {
    fauxInputEl.click();
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    headerRO?.disconnect();
  }

  return {
    destroy,
    open,
  };
}
